<template>
  <header>
    <div class="relative flex min-h-[65vh] flex-col">
      <!-- IMAGE OR VIDEO -->
      <div class="absolute inset-0 -z-10 overflow-hidden">
        <AppImage
          v-if="imageOrVideo.image"
          :src="imageOrVideo.image"
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.image.alt_fr
              : imageOrVideo.image.alt
          "
          class="size-full object-cover"
          :style="`object-position: ${imageOrVideo.image.focus_css};`"
        />

        <AppImage
          v-else-if="
            imageOrVideo.video &&
            data.hero_only_image &&
            imageOrVideo.cover_image
          "
          :alt="
            $i18n.locale === 'fr'
              ? imageOrVideo.cover_image.alt_fr
              : imageOrVideo.cover_image.alt
          "
          :src="imageOrVideo.cover_image"
          class="size-full object-cover"
          :style="`object-position: ${imageOrVideo.cover_image.focus_css};`"
        />

        <VideoPlayer
          v-else-if="imageOrVideo.video"
          :src="imageOrVideo.video"
          :autoplay="true"
          class="size-full scale-[1.2] transition-transform"
        />
        <div
          v-else
          class="size-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
        />
      </div>

      <HeroImageGradient />

      <div
        class="z-10 flex w-full grow flex-col justify-end pb-12 pt-navigation-desktop text-white"
      >
        <Container indent="center">
          <div>
            <!-- BACK LINK -->
            <button
              class="group -m-1 my-5 flex items-center gap-3 p-1 text-base ring-white ring-offset-0 sm:my-7"
              @click="$router.back()"
            >
              <ArrowGrowIcon class="-ml-2 w-6 rotate-180" />
              <span class="first-letter:uppercase">
                {{ $t('back') }}
              </span>
            </button>

            <AnimatedContainer>
              <!-- TITLE -->
              <h1
                v-if="data.heading"
                class="fade-in mb-4 max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-6 sm:text-6xl"
                v-html="$replaceHtmlTags(data.heading)"
              />

              <time
                :datetime="machineReadableDateTime"
                class="fade-in mb-7 inline-block text-white/60 sm:mb-12"
              >
                {{ dateTime }}
              </time>
            </AnimatedContainer>

            <AnimatedContainer
              class="flex flex-col flex-wrap gap-x-6 gap-y-4 sm:flex-row"
            >
              <div class="slide-in-left">
                <NuxtLink
                  class="slide-in-left"
                  :to="{
                    path: route.path.split('/').slice(0, -1).join('/'),
                    query: { newsType: data.news_type.slug },
                  }"
                >
                  <TypeTag
                    layout="medium"
                    :class="{
                      'sm:!py-3': data.hero_links && data.hero_links.length,
                    }"
                  >
                    {{ data.news_type.title }}
                  </TypeTag>
                </NuxtLink>
              </div>

              <!-- Link Buttons to e.g Sustainability Article -->
              <div
                v-for="item in data.hero_links"
                :key="item.id"
                class="slide-in-left"
              >
                <AppButton
                  v-if="item.link && item.link_text"
                  is-secondary
                  :to="item.link"
                >
                  {{ item.link_text }}
                </AppButton>
              </div>
            </AnimatedContainer>
          </div>
        </Container>
      </div>
    </div>

    <!-- LEAD -->
    <Container indent="center">
      <div
        v-if="data.lead"
        class="max-w-text pt-16 text-md font-medium"
        v-html="data.lead"
      />
    </Container>
  </header>
</template>

<script setup>
const { t, locale } = useI18n()
const route = useRoute()

const props = defineProps({
  data: { type: Object, required: true },
})

const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

const dateTime = computed(() => {
  if (!props.data?.date) {
    return null
  }

  // The date field now carries the time too, formatted as "Y-m-d H:i".
  const [datePart, timePart] = props.data.date.split(' ')

  let date = new Date(datePart).toLocaleDateString(locale.value || 'de', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Zurich',
  })

  // Only display the time when one has actually been set (not midnight).
  if (timePart && timePart !== '00:00') {
    date += `, ${timePart} ${t('hrs')}`
  }

  return date
})

const machineReadableDateTime = computed(() => {
  return props.data?.date ? props.data.date.replace(' ', 'T') : null
})
</script>
